import React, { useCallback, useMemo, useState } from "react"
import { landscapePhone } from "@/utils/landscapePhone"
import { Box, Button, Paper, Portal, Stack, Typography } from "@mui/material"
import { useTranslation } from "@/contexts/LanguageContext"

export type MissingIndexInfo = { indexUrl?: string; rawMessage?: string; filters?: string[] }

type FirestoreMissingIndexContextValue = {
  reportMissingIndex: (error: unknown, filters?: string[]) => boolean
  closeMissingIndex: () => void
}

const FirestoreMissingIndexContext = React.createContext<FirestoreMissingIndexContextValue | undefined>(undefined)

function isMissingIndexError(error: unknown): { indexUrl?: string; message: string } | null {
  const firebaseError = error as { code?: string; message?: string } | undefined
  const message = typeof firebaseError?.message === "string" ? firebaseError.message : typeof error === "string" ? error : undefined
  const code = firebaseError?.code ?? ""
  if (!message) return null
  const lower = message.toLowerCase()
  const isMissingIndex = code === "failed-precondition" || lower.includes("requires an index") || lower.includes("create it here")
  if (!isMissingIndex) return null
  const urlMatch = message.match(/https:\/\/[^\s)]+/)
  const indexUrl = urlMatch ? urlMatch[0] : undefined
  return { indexUrl, message }
}

export function FirestoreMissingIndexProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const [missingIndexInfo, setMissingIndexInfo] = useState<MissingIndexInfo | null>(null)

  const reportMissingIndex = useCallback((error: unknown, filters?: string[]) => {
    const parsed = isMissingIndexError(error)
    if (!parsed) return false
    setMissingIndexInfo({ indexUrl: parsed.indexUrl, rawMessage: parsed.message, filters })
    return true
  }, [])

  const closeMissingIndex = useCallback(() => setMissingIndexInfo(null), [])

  const value = useMemo<FirestoreMissingIndexContextValue>(
    () => ({ reportMissingIndex, closeMissingIndex }),
    [reportMissingIndex, closeMissingIndex]
  )

  const whatsappHref = useMemo(() => {
    if (!missingIndexInfo) return undefined
    const filtersLabel =
      missingIndexInfo.filters && missingIndexInfo.filters.length > 0
        ? missingIndexInfo.filters.join(", ")
        : t("propertySearch.missingIndex.filtersFallback")
    const messageTemplate = missingIndexInfo.indexUrl
      ? t("propertySearch.missingIndex.whatsappWithLink")
      : t("propertySearch.missingIndex.whatsappWithoutLink")
    const message = messageTemplate.replace("{filters}", filtersLabel).replace("{url}", missingIndexInfo.indexUrl ?? "")
    return `https://wa.me/?text=${encodeURIComponent(message)}`
  }, [missingIndexInfo, t])

  return (
    <FirestoreMissingIndexContext.Provider value={value}>
      {children}
      {missingIndexInfo && (
        <Portal>
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 1600,
              bgcolor: "rgba(15, 23, 42, 0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2
            }}
            role="presentation"
            onClick={closeMissingIndex}>
            <Paper
              elevation={8}
              sx={{
                maxWidth: 420,
                width: "100%",
                borderRadius: 2,
                p: 3,
                bgcolor: "background.paper",
                ...landscapePhone({ p: 1.5, maxHeight: "94dvh", overflowY: "auto" })
              }}
              onClick={(e) => e.stopPropagation()}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t("propertySearch.missingIndex.title")}
                  </Typography>
                  <Typography variant="body1">{t("propertySearch.missingIndex.description")}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {missingIndexInfo.filters && missingIndexInfo.filters.length > 0
                      ? missingIndexInfo.filters.join(", ")
                      : t("propertySearch.missingIndex.filtersFallback")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t("propertySearch.missingIndex.help")}
                  </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
                  <Button onClick={closeMissingIndex} variant="outlined">
                    {t("propertySearch.missingIndex.close")}
                  </Button>
                  {whatsappHref && (
                    <Button
                      onClick={() => {
                        if (typeof window !== "undefined") {
                          window.open(whatsappHref, "_blank", "noopener,noreferrer")
                        }
                        closeMissingIndex()
                      }}
                      color="success"
                      variant="contained">
                      {t("propertySearch.missingIndex.shareWhatsApp")}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Box>
        </Portal>
      )}
    </FirestoreMissingIndexContext.Provider>
  )
}

export function useFirestoreMissingIndex() {
  const context = React.useContext(FirestoreMissingIndexContext)
  if (!context) {
    throw new Error("useFirestoreMissingIndex must be used within FirestoreMissingIndexProvider")
  }
  return context
}
